import { object as yupObject, string as yupString } from 'yup'
import { useI18n } from 'vue-i18n'
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator'
import { toValue } from 'vue'
import { isEuCountry } from '@/utils/country.js'

export function useAddress() {
  const { t } = useI18n()

  function canBeSetAsStandardAddress(countryCode) {
    return isEuCountry(countryCode)
  }

  function getAddressValidationSchema(addressForm) {
    return yupObject().shape({
      firstName: yupString()
        .required(t('validation.required', { fieldName: t('customer.form.label.first_name') }))
        .matches(/^[^0-9^;:+*=?.$!]*$/, t('validation.no_numbers_allowed')),
      lastName: yupString()
        .required(t('validation.required', { fieldName: t('customer.form.label.last_name') }))
        .matches(/^[^0-9^;:+*=?.$!]*$/, t('validation.no_numbers_allowed')),
      street: yupString()
        .default('')
        .nullable()
        .notRequired()
        .test('is-street-valid', t('validation.invalid_street'), function (value) {
          return /^(?=.*\d)(?=.*[A-Za-z]).{3,}$/.test(value)
        }),
      addition: yupString()
        .default('')
        .nullable()
        .notRequired()
        .test(
          'is-packstation-postnummer-valid',
          t('validation.packstation_postnummer_required'),
          function (value) {
            const street = toValue(addressForm)?.street || ''
            const countryCode = toValue(addressForm)?.countryCode

            // Only validate if country is DE and street starts with "Packstation"
            if (
              countryCode === 'DE' &&
              street &&
              street.trim().toLowerCase().startsWith('packstation')
            ) {
              if (!value || value.trim() === '') {
                return false
              }
              // Check if value contains only digits and has length between 6 and 10
              const trimmedValue = value.trim()
              return /^\d{6,10}$/.test(trimmedValue)
            }
            return true
          }
        ),
      postalCode: yupString()
        .required(t('validation.required', { fieldName: t('customer.form.label.post_code') }))
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .test('is-postal-code-valid', t('validation.invalid_postal_code'), function (value) {
          value = value.trim()
          if (value) {
            const countryCode = toValue(addressForm)?.countryCode
            if (countryCode && postcodeValidatorExistsForCountry(countryCode)) {
              return postcodeValidator(value, countryCode)
            } else {
              return /\d{2}/.test(value)
            }
          }
          return true
        }),
      city: yupString()
        .required(t('validation.required', { fieldName: t('customer.form.label.city') }))
        .test(
          'is-city-valid',
          t('validation.required', { fieldName: 'customer.form.label.city' }),
          function (value) {
            return !value || (value && value.trim().length > 0)
          }
        )
    })
  }

  return {
    getAddressValidationSchema,
    canBeSetAsStandardAddress
  }
}
